import { Component, OnInit } from '@angular/core';
import { AppConfigService } from '../app-config.service';
import * as PowerBI from 'powerbi-client';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '../authentication.service';
import { DashboardPlugin } from './dashboard-plugin.interface';
import { DownloadPlugin } from './download-plugin';
import { SelectionFixPlugin } from './selection-fix-plugin';
import { AcquisitionDataPlugin } from './acquisition-data-plugin';
import { BackendApiService } from '../backendApi.service';
import { MessageService } from '@slb-dls/angular-material/notification';
import { MatDialog } from '@angular/material/dialog';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    standalone: false
})
export class DashboardComponent implements OnInit {
  private currentPage: string;
  private reportLoaded = false;
  private report: PowerBI.Embed;

  private plugins: DashboardPlugin[];

  private currentReportId = '';
  private token = '';

  private routablePages: string[] = ['Dashboard', 'ByWells', 'byUploads', 'SandBox'];
  acquisitionContainers: string[] = [];

  constructor(private appConfig: AppConfigService,
              private route: ActivatedRoute,
              private authenticationService: AuthenticationService,
              private router: Router,
              private backendApiService: BackendApiService,
              private messageService: MessageService,
              private dialog: MatDialog) {
      this.route.paramMap.subscribe(async params => {
        const page = params.get('page') || 'Dashboard';
        if (!this.reportLoaded && !this.routablePages.includes(page)) {
          this.navigateTo(this.routablePages[0]);
          return;
        }

        if (this.currentPage !== page) {
          this.currentPage = page;
          this.setPage(page);
        }
      });
  }

  async ngOnInit() {
    this.plugins = [
      new DownloadPlugin(this.backendApiService, this.appConfig.containerDownloadUrl, this.messageService, this.dialog),
      new SelectionFixPlugin(),
      new AcquisitionDataPlugin(this.backendApiService, containers => this.acquisitionContainers = containers)
    ];

    this.token = this.authenticationService.getToken();

    const container = document.getElementById('dashboard-report');
    window.powerbi.bootstrap(container, { type: 'report', hostname: this.appConfig.powerBiHostname });

    this.currentReportId = this.appConfig.reportId;
    this.initializeReport(this.currentReportId);
  }

  private initializeReport(reportId: string) {
    const container = document.getElementById('dashboard-report');

    const embedConfiguration = {
      type: 'report',
      accessToken: this.token,
      tokenType: PowerBI.models.TokenType.Aad,
      embedUrl: `${this.appConfig.powerBiHostname}/reportEmbed?reportId=${reportId}&groupId=${this.appConfig.groupId}`,
      id: reportId,
      settings: <PowerBI.IEmbedSettings>{
        visualRenderedEvents: true,
        panes: {
          filters: { visible: false },
          pageNavigation: { visible: false }
        }
      }
    };

    this.report = window.powerbi.embed(container, embedConfiguration);

    this.report.on('loaded', async () => {
      this.report.off('loaded');
      this.reportLoaded = true;
      this.setPage(this.currentPage);
    });

    this.report.on('pageChanged', async (event) => {
      if (!this.reportLoaded) {
        return;
      }

      const newPage = (event.detail as any).newPage.displayName;
      this.currentPage = newPage;
      this.navigateTo(newPage);
    });

    this.plugins.forEach(x => x.register(this.report));
  }

  private navigateTo(pageName: string) {
      this.router.navigate(['dashboard', pageName]);
  }


  private async setPage(pageName: string) {
    if (!this.reportLoaded) {
      return;
    }

    const reportId = pageName === 'CoilScan' ? this.appConfig.coilScanReportId : this.appConfig.reportId;

    if (reportId !== this.currentReportId) {
      if (this.report) {
        this.report.off('pageChanged');
      }

      this.reportLoaded = false;
      this.currentReportId = reportId;
      this.currentPage = pageName;
      this.initializeReport(this.currentReportId);
    }

    let reportDiv = document.getElementById('dashboard-report');
    const report = window.powerbi.get(reportDiv) as any;
    let pages = await report.getPages();
    pages.filter(x => x.displayName === pageName)[0].setActive();
  }
}
